/**
 * ManagerDigest — the morning email each store manager gets: their open and
 * overdue actions so nothing slips. DEMO: "Send" simulates the morning send;
 * wire an email provider (Resend) + real manager emails to send for real.
 */
import React, { useMemo } from 'react';
import { Mail, AlertTriangle, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { isOverdue, relativeDue } from './qsrTasks';

export default function ManagerDigest({ tasks = [] }) {
  const groups = useMemo(() => {
    const open = tasks.filter((t) => t.status !== 'done');
    const byOwner = {};
    open.forEach((t) => {
      const key = t.owner || 'Unassigned';
      (byOwner[key] = byOwner[key] || { owner: key, store: t.store, items: [], overdue: 0 }).items.push(t);
      if (isOverdue(t)) byOwner[key].overdue += 1;
    });
    return Object.values(byOwner).sort((a, b) => b.overdue - a.overdue);
  }, [tasks]);

  const send = () => {
    if (!groups.length) { toast.info('Nothing to send', { description: 'No open actions right now.' }); return; }
    toast.success('Morning digest sent', { description: `Emailed ${groups.length} manager${groups.length > 1 ? 's' : ''} their open & overdue actions.` });
  };

  const today = new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="qsr-card rounded-3xl p-5 sm:p-6" data-testid="manager-digest">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sky-600 text-xs font-semibold"><Mail className="w-4 h-4" /> Manager digest</div>
          <h3 className="text-lg font-semibold text-slate-900 mt-1">Morning email to each store manager</h3>
          <p className="text-xs text-slate-500 mt-0.5">Sends at 6:00 AM daily · their open &amp; overdue actions so nothing slips</p>
        </div>
        <button onClick={send} className="shrink-0 inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold px-3 py-2 bg-sky-600 text-white hover:bg-sky-700 transition-colors" data-testid="digest-send">
          <Send className="w-3.5 h-3.5" /> Send morning digest
        </button>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {groups.length === 0 && (
          <div className="text-sm text-slate-500 sm:col-span-2 py-4 text-center">No open actions — every manager is clear. 🎉</div>
        )}
        {groups.map((g) => (
          <div key={g.owner} className="rounded-2xl border border-slate-200/70 bg-white/70 overflow-hidden" data-testid={`digest-manager-${g.owner.replace(/\W+/g, '-').toLowerCase()}`}>
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
              <div className="text-[11px] text-slate-400">To: {g.owner.split(' ')[0].toLowerCase()}@thedailygrill.com.au</div>
              <div className="text-sm font-semibold text-slate-900">Your Daily Grill actions — {today}</div>
              <div className="text-[11px] text-slate-500">{g.owner} · {g.store}
                {g.overdue > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 text-rose-600 font-semibold"><AlertTriangle className="w-3 h-3" />{g.overdue} overdue</span>}
              </div>
            </div>
            <div className="p-3 space-y-1.5">
              {g.items.map((t) => {
                const od = isOverdue(t);
                return (
                  <div key={t.id} className="flex items-center gap-2 text-sm">
                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${od ? 'bg-rose-500' : 'bg-amber-400'}`} />
                    <span className="flex-1 min-w-0 truncate text-slate-700">{t.title}</span>
                    <span className={`shrink-0 inline-flex items-center gap-0.5 text-[11px] font-medium ${od ? 'text-rose-600' : 'text-slate-400'}`}><Clock className="w-3 h-3" />{relativeDue(t.due).label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-400">Demo preview — connect an email provider to deliver these for real.</div>
    </div>
  );
}
